import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import { addBooker, updateBusiness, updateDriver } from './contextDropDownsSlice'
import { Booker, Business } from './types'
import { DriverListType } from '@view/contexts/FodContainerContext/types'
import { fetchInitContext } from './thunks'
import { setVehicleGroup } from './resVehicleSlice'
import { formatTaxiVehicles } from '../services/ajax'
import { setDisplayBackToRentalButton } from './resUiSlice'

type INITIAL_STATE = {
	businessId: string
	bookerId: string
	driverId: string
	numberOfPassengers: number
	from: null | { address1: string; lat: string; long: string; postcode: string; country: string }
	to: null | { address1: string; lat: string; long: string; postcode: string; country: string }
	pickUpDate: string
	pickUpTime: string
	fodRideVehicles: {}
	pickUpDateString: string
}

type FormValues = Omit<INITIAL_STATE, 'businessId' | 'bookerId' | 'driverId'>

const initialState: INITIAL_STATE = {
	businessId: '',
	bookerId: '',
	driverId: '',
	numberOfPassengers: 1,
	from: null,
	to: null,
	pickUpDate: '',
	pickUpTime: '',
	fodRideVehicles: {},
	directionsApiLocations: { from: {}, to: {} },
}

export const fodRideSlice = createSlice({
	name: 'fodRide',
	initialState,
	reducers: {
		setDistance(state, action) {
			function formatKmToMiles(distance = '') {
				const [number] = distance.split(' ')

				return `${Number(number * 0.621371).toFixed(2)} miles`
			}

			state.distance = formatKmToMiles(action.payload)
		},
		updateAddressText(state, action) {
			const { name, value } = action.payload

			state[`${name}Text`] = value
		},
		updatePickUpDropOff(state, action) {
			const { from, to, directionsApi, count } = action.payload

			//check first to see if lat is coming from directions api
			if (directionsApi) {
				if (count == 1) {
					const directions = {
						from: {
							address1: from.address1,
							lat: from.lat,
						},
						to: {
							address1: to.address1,
							lat: to.lat,
						},
					}

					state.directionsApiLocations = directions

					return
				}

				if (state.directionsApiLocations.from.lat != from.lat) {
					state.directionsApiLocations.from.lat = from.lat
					state.fromText = from.address1
					state.from = from
					return
				}

				if (state.directionsApiLocations.to.lat != to.lat) {
					state.directionsApiLocations.to.lat = to.lat
					state.toText = to.address1
					state.to = to
					return
				}
			}

			state.from = from
			state.to = to
			state.selectedAddress = from
			state.fromText = from.address1
			state.toText = to.address1
		},
		setFodRideVehicles(state, action) {
			const { message, bookingFee, ...otherProps } = action.payload
			state.fodRideVehicles = { ...otherProps }
			state.fodRideMessage = message

			if (bookingFee) {
				state.bookingFee = bookingFee.toString()
			}
		},
		setBooker(state, action: PayloadAction<Booker>) {
			const { id } = action.payload
			state.bookerId = id
		},
		setFieldValues(state, action: PayloadAction<{ key: keyof FormValues; inputValue: string; resetAddress?: boolean }>) {
			const { key, inputValue, resetAddress } = action.payload

			if (key == 'pickUpDate') {
				const selectedDate = new Date(inputValue)
				const selectedDateToString = selectedDate.toLocaleDateString()
				const todaysDate = new Date()
				const todaysDateString = todaysDate.toLocaleDateString()

				if (selectedDateToString == todaysDateString) {
					const todaysDateWithMinutesAdded = addMinutes(todaysDate, 15)
					const roundUpTo5Minutes = roundTo(1000 * 60 * 5)(todaysDateWithMinutesAdded)
					const roundUpTo5MinutesDate = new Date(roundUpTo5Minutes)
					const todaysDateHour = roundUpTo5MinutesDate.getHours()
					const todaysDateMinutes = roundUpTo5MinutesDate.getMinutes()
					const [digitOneMinute, digitTwoMinute] = todaysDateMinutes.toString().split('')
					const [digitOneHour, digitTwoHour] = todaysDateHour.toString().split('')

					const todaysDateDefaultTime = `${addZeroToTime(digitOneHour, digitTwoHour)}:${addZeroToTime(
						digitOneMinute,
						digitTwoMinute
					)}`

					state.pickUpTime = todaysDateDefaultTime
				} else {
					state.pickUpTime = '12:00'
				}

				state[key] = selectedDate
				state.pickUpDateString = selectedDateToString
				return
			}

			if (key == 'from' || key == 'to') {
				if (resetAddress) {
					state[key] = null
					return
				}
				const { address: address1, latitude: lat, longitude: long, postcode, countryName: country } = inputValue

				state[key] = { address1, lat, long, postcode, country }
				state.country = country
				state.selectedAddress = address1
				state[`${key}Text`] = address1
				return
			}

			state[key] = inputValue
		},

		clearField(state, action: PayloadAction<'booker' | 'driver'>) {
			state[`${action.payload}Id`] = ''
		},
		updateFodRideVehicles(state, action) {
			const taxis = formatTaxiVehicles(action.payload)

			state.fodRideVehicles = { businessVehicleCodesReact: { car: taxis } }
		},
		setFlexible(state, action) {
			const isToggled = action.payload

			if (isToggled) {
				delete state.pickUpTime
				state.isFlexible = 1
			} else {
				state.pickUpTime = ''
				if (state.isFlexible) {
					delete state.isFlexible
				}
			}
		},
	},
	extraReducers(builder) {
		builder.addCase(addBooker, (state, action) => {
			if (action.payload.newBooker) {
				delete state.bookerId
			}
		}),
			builder.addCase(setDisplayBackToRentalButton, (state, action) => {
				if (!action.payload) {
					state.fodRideVehicles = {}
					state.bookingFee = ''
				}
			}),
			builder.addCase(setVehicleGroup, (state, action) => {
				const { id, businessId, contactId, driverId, tariffBandId, taxiVehicle } = action.payload

				if (taxiVehicle) {
					state.vehicleSelected = convertObjValuesToNumber({ id, businessId, contactId, driverId, tariffBandId })
				}
			}),
			builder.addCase(updateBusiness, (state, action: PayloadAction<Business>) => {
				const { businessId } = action.payload

				if (businessId) {
					state.businessId = businessId.toString()
				}
			}),
			builder.addCase(updateDriver, (state, action: PayloadAction<DriverListType>) => {
				if (action.payload?.driverId) {
					const { driverId } = action.payload
					state.driverId = driverId
				}
			}),
			builder.addCase(fetchInitContext.fulfilled, (state, action) => {
				const { context } = action.payload

				if (context.dashType == 'cui') {
					const { driver, currentBusinessId } = context

					state.businessId = currentBusinessId
					state.driverId = driver?.id
				}
			})
	},
})

export function addZeroToTime(firstDigit, secondDigit) {
	if (!firstDigit) return `${secondDigit}0`

	if (!secondDigit) return `0${firstDigit}`

	return `${firstDigit}${secondDigit}`
}

export const roundTo = roundTo => x => Math.ceil(x / roundTo) * roundTo

export function addMinutes(date: Date, minutes: number) {
	date.setMinutes(date.getMinutes() + minutes)
	return date
}

export function convertObjValuesToNumber(obj: { [key: string]: string }) {
	const newObj = { ...obj }
	for (const key in newObj) {
		newObj[key] = Number(newObj[key])
	}

	return newObj
}

export const selectFodRide = state => state.fodRide

export const {
	setBooker,
	setFieldValues,
	clearField,
	setFodRideVehicles,
	updateFodRideVehicles,
	updatePickUpDropOff,
	updateAddressText,
	setDistance,
	setFlexible,
} = fodRideSlice.actions

export const fodRideSliceReducer = fodRideSlice.reducer
